import React, { useState, useEffect } from 'react';
import { Plan } from '../core/_models';
import { createPlan, updatePlan } from '../core/subscriptionService';
import { getTypeServicesAll } from '../../../services/typeServices';
import { getProductsAll } from '../../../services/productsService';
import Select from 'react-select';
import { useToast } from '../../components/Toats';

interface Props {
  show: boolean;
  handleClose: () => void;
  plan?: Plan | null;
}

export const PlanForm: React.FC<Props> = ({ show, handleClose, plan }) => {
  const { toggleToast } = useToast();
  const [name, setName] = useState('');
  const [periodicity, setPeriodicity] = useState<string>('MENSAL');
  const [baseValue, setBaseValue] = useState<number>(0);
  const [adhesionFee, setAdhesionFee] = useState<number>(0);

  const [hasMaintenance, setHasMaintenance] = useState(false);
  const maintenanceCost = 45;

  const [activePanelMultiplier, setActivePanelMultiplier] = useState(false);
  const [panelCost, setPanelCost] = useState(2.50);
  const [simulatedPanels, setSimulatedPanels] = useState(12);
  const [simulatedKwp, setSimulatedKwp] = useState(5.5);

  const [saving, setSaving] = useState(false);
  
  // New Dynamic States
  const [servicesOptions, setServicesOptions] = useState<any[]>([]);
  const [productsOptions, setProductsOptions] = useState<any[]>([]);
  
  type PlanItemState = {
    id?: string;
    local_id: string;
    item_type: 'SERVICO' | 'PRODUTO';
    typeService_id?: string;
    product_id?: string;
    name: string;
    unit_price: number;
    quantity_mode: 'FIXED' | 'PER_PANEL' | 'PER_KWP';
  };
  const [items, setItems] = useState<PlanItemState[]>([]);

  useEffect(() => {
    getTypeServicesAll().then(res => setServicesOptions(res || []));
    getProductsAll().then(res => setProductsOptions(res || []));
  }, []);

  useEffect(() => {
    if (plan) {
      setName(plan.name || '');
      setPeriodicity(plan.periodicity || 'MENSAL');
      setBaseValue(Number(plan.base_value) || 0);
      setAdhesionFee(Number(plan.adhesion_fee) || 0);
      if (plan.planItems && plan.planItems.length > 0) {
        setItems(plan.planItems.map((i: any) => {
          const typeId = i.typeService?.id || i.typeService || i.typeService_id;
          const prodId = i.product?.id || i.product || i.product_id;
          
          let resolvedName = 'Item';
          if (typeId) {
             const found = servicesOptions.find(s => s.id === typeId);
             if (found) resolvedName = found.description || found.name || 'Item';
             else resolvedName = i.typeService?.description || i.typeService?.name || 'Item';
          } else if (prodId) {
             const found = productsOptions.find(p => p.id === prodId);
             if (found) resolvedName = found.name || found.description || 'Item';
             else resolvedName = i.product?.name || i.product?.description || 'Item';
          }

          return {
            id: i.id,
            local_id: Math.random().toString(36).substring(7),
            item_type: i.item_type === 'PRODUTO' || i.item_type === 'PRODUCT' ? 'PRODUTO' : 'SERVICO',
            typeService_id: typeId,
            product_id: prodId,
            name: resolvedName,
            unit_price: Number(i.unit_price) || 0,
            quantity_mode: i.quantity_mode || 'FIXED'
          };
        }));
      } else {
        setItems([]);
      }
    } else {
      setName('');
      setPeriodicity('MENSAL');
      setBaseValue(0);
      setAdhesionFee(0);
      setItems([]);
    }
  }, [plan, servicesOptions, productsOptions]);

  const handleSave = () => {
    if (!name.trim()) {
      toggleToast('Informe o nome do plano.', 'error');
      return;
    }
    if (items.length === 0) {
      toggleToast('Adicione ao menos um serviço ou produto ao plano.', 'error');
      return;
    }

    const payload = {
      name,
      base_value: baseValue,
      adhesion_fee: adhesionFee,
      status: 'ATIVO',
      periodicity,
      items: items.map(i => ({
        id: i.id,
        item_type: i.item_type,
        default_quantity: 1,
        quantity_mode: i.quantity_mode,
        is_required: true,
        unit_price: Number(i.unit_price),
        product_id: i.product_id || null,
        typeService_id: i.typeService_id || null
      }))
    };
    setSaving(true);
    const request = plan ? updatePlan(plan.id, payload) : createPlan(payload);
    request.then(() => {
      toggleToast('Plano salvo com sucesso!', 'success');
      handleClose();
    }).catch((err: any) => {
      toggleToast(err?.response?.data?.message || err?.response?.data?.error || 'Erro ao salvar plano.', 'error');
    }).finally(() => setSaving(false));
  };

  if (!show) {
    return null;
  }

  const fixedItemsValue = items.filter(i => i.quantity_mode === 'FIXED').reduce((acc, curr) => acc + Number(curr.unit_price), 0);
  const multiplierItemsValue = items.filter(i => i.quantity_mode === 'PER_PANEL').reduce((acc, curr) => acc + Number(curr.unit_price), 0);
  const kwpItemsValue = items.filter(i => i.quantity_mode === 'PER_KWP').reduce((acc, curr) => acc + Number(curr.unit_price), 0);
  
  const calculatedBase = baseValue + fixedItemsValue;
  const calculatedVariable = (multiplierItemsValue > 0 ? (simulatedPanels * multiplierItemsValue) : 0) + (kwpItemsValue > 0 ? (simulatedKwp * kwpItemsValue) : 0);
  const calculatedTotal = calculatedBase + calculatedVariable;

  const addItem = (item: any, type: 'SERVICO' | 'PRODUTO') => {
    setItems(prev => [
      ...prev,
      {
        local_id: Math.random().toString(36).substring(7),
        item_type: type,
        typeService_id: type === 'SERVICO' ? item.value : undefined,
        product_id: type === 'PRODUTO' ? item.value : undefined,
        name: item.label,
        unit_price: item.preco || 0,
        quantity_mode: 'FIXED'
      }
    ]);
  };

  const removeItem = (local_id: string) => {
    setItems(prev => prev.filter(i => i.local_id !== local_id));
  };

  const updateItem = (local_id: string, field: string, value: any) => {
    setItems(prev => prev.map(i => i.local_id === local_id ? { ...i, [field]: value } : i));
  };

  const serviceOpts = servicesOptions.map(s => ({ value: s.id, label: s.description || s.name, preco: s.preco }));
  const productOpts = productsOptions.map(p => ({ value: p.id, label: p.description || p.name, preco: p.preco }));

  return (
    <div className='w-100'>
      <div className='d-flex align-items-center justify-content-between mb-8'>
        <div className='d-flex align-items-center'>
          <button className='btn btn-icon btn-light-primary me-4' onClick={handleClose}>
            <i className='bi bi-arrow-left fs-2'></i>
          </button>
          <h2 className='fw-bolder fs-1 mb-0'>{plan ? 'Editar Plano' : 'Novo Plano de Assinatura'}</h2>
        </div>
      </div>

      <div className='row g-10'>
        <div className='col-lg-8'>
          <div className='card border border-dashed border-gray-300'>
            <div className='card-body p-9'>
              <h3 className='mb-6'>Informações Principais</h3>
              <div className='row g-6 mb-8'>
                <div className='col-md-6'>
                  <label className='form-label fw-bolder text-gray-800'>Nome do Plano</label>
                  <input type='text' className='form-control form-control-solid' value={name} onChange={e => setName(e.target.value)} placeholder='Ex: Solar Premium' />
                </div>
                <div className='col-md-3'>
                  <label className='form-label fw-bolder text-gray-800'>Valor Base Mensal</label>
                  <div className='input-group input-group-solid'>
                    <span className='input-group-text'>R$</span>
                    <input type='number' className='form-control form-control-solid' value={baseValue} onChange={e => setBaseValue(Number(e.target.value))} />
                  </div>
                </div>
                <div className='col-md-3'>
                  <label className='form-label fw-bolder text-gray-800'>Taxa de Adesão</label>
                  <div className='input-group input-group-solid'>
                    <span className='input-group-text'>R$</span>
                    <input type='number' className='form-control form-control-solid' value={adhesionFee} onChange={e => setAdhesionFee(Number(e.target.value))} />
                  </div>
                </div>
                <div className='col-md-3'>
                  <label className='form-label fw-bolder text-gray-800'>Periodicidade</label>
                  <select
                    className='form-select form-select-solid'
                    value={periodicity}
                    onChange={e => setPeriodicity(e.target.value)}
                  >
                    <option value='MENSAL'>Mensal</option>
                    <option value='TRIMESTRAL'>Trimestral</option>
                    <option value='SEMESTRAL'>Semestral</option>
                    <option value='ANUAL'>Anual</option>
                  </select>
                </div>
              </div>

              <div className='separator separator-dashed my-8'></div>

              <h3 className='mb-6'>Serviços & Modificadores</h3>
              <span className='text-muted d-block mb-4'>Integração dinâmica de serviços e produtos no plano.</span>

              <div className='row g-4 mb-8'>
                <div className='col-md-6'>
                  <Select
                    options={serviceOpts}
                    onChange={(val) => val && addItem(val, 'SERVICO')}
                    placeholder='Adicionar Serviço...'
                    isSearchable
                    value={null}
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </div>
                <div className='col-md-6'>
                  <Select
                    options={productOpts}
                    onChange={(val) => val && addItem(val, 'PRODUTO')}
                    placeholder='Adicionar Produto...'
                    isSearchable
                    value={null}
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </div>
              </div>

              <div className='d-flex flex-column gap-5'>
                {items.length === 0 && (
                  <div className='text-center text-muted p-5 border border-dashed rounded border-gray-300'>
                    Nenhum item adicionado. Selecione acima.
                  </div>
                )}
                {items.map(item => (
                  <div key={item.local_id} className={`border rounded p-5 border-gray-300`}>
                    <div className='d-flex align-items-center justify-content-between mb-4'>
                      <div className='d-flex align-items-center'>
                        <i className={`bi fs-2x me-4 ${item.item_type === 'SERVICO' ? 'bi-shield-check text-primary' : 'bi-box text-success'}`}></i>
                        <div className='d-flex flex-column'>
                          <span className='fw-bolder fs-5'>{item.name}</span>
                          <span className='text-muted fw-bold fs-7'>{item.item_type === 'SERVICO' ? 'Serviço' : 'Produto'}</span>
                        </div>
                      </div>
                      <button className='btn btn-icon btn-light-danger btn-sm' onClick={() => removeItem(item.local_id)}>
                        <i className='bi bi-trash fs-5'></i>
                      </button>
                    </div>

                    <div className='d-flex flex-wrap align-items-center gap-4 bg-light rounded p-4'>
                      <div className='d-flex flex-column gap-1 flex-grow-1'>
                        <span className='fw-bold fs-7 text-muted'>Multiplicador</span>
                        <select 
                          className='form-select form-select-sm form-select-solid w-200px'
                          value={item.quantity_mode}
                          onChange={e => updateItem(item.local_id, 'quantity_mode', e.target.value)}
                        >
                          <option value='FIXED'>Quantidade Fixa</option>
                          <option value='PER_PANEL'>Por Número de Painéis</option>
                          <option value='PER_KWP'>Por kWp Instalado</option>
                        </select>
                      </div>

                      <div className='d-flex flex-column gap-1'>
                        <span className='fw-bold fs-7 text-muted'>Preço Unitário (R$)</span>
                        <div className='input-group input-group-sm input-group-solid w-150px'>
                          <span className='input-group-text'>R$</span>
                          <input 
                            type='number' 
                            className='form-control form-control-solid' 
                            value={item.unit_price} 
                            onChange={e => updateItem(item.local_id, 'unit_price', Number(e.target.value))} 
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className='col-lg-4'>
          <div className='card border border-dashed border-gray-300 sticky-top' style={{ top: '30px' }}>
            <div className='card-body p-9'>
              <h3 className='text-gray-800 mb-6'>Simulador de Receita</h3>

              <div className='mb-8'>
                <label className='form-label fw-bolder text-gray-500'>Tamanho da Usina do Cliente (Para Teste)</label>
                <div className='d-flex gap-3'>
                  <div className='input-group input-group-solid border border-gray-300 rounded'>
                    <input
                      type='number'
                      className='form-control'
                      value={simulatedPanels}
                      onChange={e => setSimulatedPanels(Number(e.target.value))}
                      disabled={multiplierItemsValue === 0}
                    />
                    <span className='input-group-text border-0'>Painéis</span>
                  </div>
                  <div className='input-group input-group-solid border border-gray-300 rounded'>
                    <input
                      type='number'
                      step='0.1'
                      className='form-control'
                      value={simulatedKwp}
                      onChange={e => setSimulatedKwp(Number(e.target.value))}
                      disabled={kwpItemsValue === 0}
                    />
                    <span className='input-group-text border-0'>kWp</span>
                  </div>
                </div>
              </div>

              <div className='separator separator-dashed border-gray-300 mb-8'></div>

              <div className='d-flex justify-content-between align-items-center mb-4'>
                <span className='text-gray-600 fw-bold'>Valor Base Mensal</span>
                <span className='text-gray-800 fw-bolder'>R$ {(baseValue || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
              </div>

              {items.filter(i => i.quantity_mode === 'FIXED').map(item => (
                <div key={`sim-${item.local_id}`} className='d-flex justify-content-between align-items-center mb-4'>
                  <span className='text-gray-600 fw-bold'>{item.name}</span>
                  <span className='text-gray-800 fw-bolder'>+ R$ {Number(item.unit_price).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                </div>
              ))}

              {items.filter(i => i.quantity_mode === 'PER_PANEL').map(item => (
                <div key={`sim-${item.local_id}`} className='d-flex justify-content-between align-items-center mb-4'>
                  <span className='text-gray-600 fw-bold'>{item.name} ({simulatedPanels} painéis)</span>
                  <span className='text-gray-800 fw-bolder'>+ R$ {(Number(item.unit_price) * simulatedPanels).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                </div>
              ))}

              {items.filter(i => i.quantity_mode === 'PER_KWP').map(item => (
                <div key={`sim-${item.local_id}`} className='d-flex justify-content-between align-items-center mb-4'>
                  <span className='text-gray-600 fw-bold'>{item.name} ({simulatedKwp} kWp)</span>
                  <span className='text-gray-800 fw-bolder'>+ R$ {(Number(item.unit_price) * simulatedKwp).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                </div>
              ))}

              <div className='d-flex justify-content-between align-items-center bg-light-primary rounded p-4 mt-6'>
                <div className='d-flex flex-column'>
                  <span className='text-primary fw-bolder fs-4'>Faturamento Final</span>
                  <span className='text-primary fs-7'>Recorrente mensal</span>
                </div>
                <span className='text-primary fw-bolder fs-2'>R$ {(calculatedTotal || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
              </div>

              {adhesionFee > 0 && (
                <div className='d-flex justify-content-between align-items-center bg-light-success rounded p-4 mt-3'>
                  <div className='d-flex flex-column'>
                    <span className='text-success fw-bolder fs-5'>Total Checkout (1ª Fatura)</span>
                    <span className='text-success fs-7'>Mensalidade + Taxa de Adesão</span>
                  </div>
                  <span className='text-success fw-bolder fs-3'>R$ {((calculatedTotal || 0) + adhesionFee).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                </div>
              )}

              <div className='mt-10 d-flex flex-column gap-3'>
                <button className='btn btn-primary w-100 fw-bolder' onClick={handleSave} disabled={saving || !name.trim() || items.length === 0}>
                  <i className='bi bi-check2 fs-3 me-2'></i> {saving ? 'Salvando...' : 'Salvar Plano'}
                </button>
                <button className='btn btn-light w-100 fw-bolder' onClick={handleClose} disabled={saving}>
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
